import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Loader2,
  Download,
  Search,
  Filter,
  X,
  ChevronDown,
  ChevronUp,
  Calendar,
  User,
  FileText,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useAuditLogs,
  type AuditLogRow,
  type AuditLogCategory,
  type AuditLogFilters,
} from '@/hooks/useAuditLogs';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { SectionCard } from './ClinicGeneral';

export function AuditLogs() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();

  const canView = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  // Filters
  const [filters, setFilters] = useState<AuditLogFilters>({
    limit: 50,
    offset: 0,
  });
  const [showFilters, setShowFilters] = useState(false);
  const [searchInput, setSearchInput] = useState('');

  const { data, isLoading, error } = useAuditLogs(canView ? activeClinicId : null, filters);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, search: searchInput || undefined, offset: 0 }));
  };

  const handleFilterChange = (key: keyof AuditLogFilters, value: unknown) => {
    setFilters((prev) => ({ ...prev, [key]: value || undefined, offset: 0 }));
  };

  const handleNextPage = () => {
    setFilters((prev) => ({ ...prev, offset: (prev.offset ?? 0) + (prev.limit ?? 50) }));
  };

  const handlePrevPage = () => {
    setFilters((prev) => ({
      ...prev,
      offset: Math.max(0, (prev.offset ?? 0) - (prev.limit ?? 50)),
    }));
  };

  const handleExport = () => {
    if (!data?.logs) return;
    
    // Convert to CSV
    const headers = [
      'Date',
      'Category',
      'Action',
      'Actor',
      'Target Type',
      'Target',
      'Source',
      'Purpose',
    ];
    const rows = data.logs.map((log) => [
      new Date(log.created_at).toLocaleString(),
      log.category,
      log.action,
      log.actor_email || 'System',
      log.target_type,
      log.target_identifier || log.target_id || '-',
      log.source,
      log.purpose || '-',
    ]);

    const csv = [headers, ...rows].map((row) => row.map((cell) => `"${cell}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (!canView) {
    return (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 dark:border-amber-800 dark:bg-amber-900/20">
        <p className="text-sm text-amber-800 dark:text-amber-300">
          {t('audit.admin_required')}
        </p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
      </div>
    );
  }

  if (error) return <ErrorAlert message={t('audit.load_error')} />;

  const hasFilters =
    filters.category ||
    filters.action ||
    filters.actor_id ||
    filters.target_type ||
    filters.start_date ||
    filters.end_date;

  return (
    <div className="space-y-6">
      <SectionCard
        title={t('audit.title')}
        subtitle={t('audit.subtitle')}
      >
        {/* Search and Filter Bar */}
        <div className="mb-6 space-y-4">
          <form onSubmit={handleSearch} className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder={t('audit.search_placeholder')}
                className="w-full rounded-lg border border-gray-300 py-2 pl-10 pr-4 text-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
              />
            </div>
            <button
              type="submit"
              className="rounded-lg bg-indigo-600 px-4 py-2 text-sm font-semibold text-white hover:bg-indigo-500"
            >
              {t('audit.search')}
            </button>
            <button
              type="button"
              onClick={() => setShowFilters(!showFilters)}
              className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              <Filter className="h-4 w-4" />
              {t('audit.filters')}
              {showFilters ? (
                <ChevronUp className="h-4 w-4" />
              ) : (
                <ChevronDown className="h-4 w-4" />
              )}
            </button>
            {data?.logs && data.logs.length > 0 && (
              <button
                type="button"
                onClick={handleExport}
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
              >
                <Download className="h-4 w-4" />
                {t('audit.export')}
              </button>
            )}
          </form>

          {/* Filter Panel */}
          {showFilters && (
            <div className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/50 md:grid-cols-2 lg:grid-cols-4">
              {/* Category Filter */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('audit.filter_category')}
                </label>
                <select
                  value={filters.category ?? ''}
                  onChange={(e) =>
                    handleFilterChange('category', e.target.value as AuditLogCategory)
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">{t('audit.all_categories')}</option>
                  <option value="authentication">{t('audit.category_authentication')}</option>
                  <option value="data_access">{t('audit.category_data_access')}</option>
                  <option value="data_modification">{t('audit.category_data_modification')}</option>
                  <option value="cpr_access">{t('audit.category_cpr_access')}</option>
                  <option value="export">{t('audit.category_export')}</option>
                  <option value="configuration">{t('audit.category_configuration')}</option>
                  <option value="security">{t('audit.category_security')}</option>
                </select>
              </div>

              {/* Target Type Filter */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('audit.filter_target_type')}
                </label>
                <select
                  value={filters.target_type ?? ''}
                  onChange={(e) => handleFilterChange('target_type', e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                >
                  <option value="">{t('audit.all_types')}</option>
                  <option value="patient">{t('audit.type_patient')}</option>
                  <option value="appointment">{t('audit.type_appointment')}</option>
                  <option value="invoice">{t('audit.type_invoice')}</option>
                  <option value="journal_entry">{t('audit.type_journal')}</option>
                  <option value="document">{t('audit.type_document')}</option>
                </select>
              </div>

              {/* Start Date */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('audit.filter_start_date')}
                </label>
                <input
                  type="date"
                  value={filters.start_date?.split('T')[0] ?? ''}
                  onChange={(e) =>
                    handleFilterChange('start_date', e.target.value ? `${e.target.value}T00:00:00Z` : undefined)
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* End Date */}
              <div>
                <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">
                  {t('audit.filter_end_date')}
                </label>
                <input
                  type="date"
                  value={filters.end_date?.split('T')[0] ?? ''}
                  onChange={(e) =>
                    handleFilterChange('end_date', e.target.value ? `${e.target.value}T23:59:59Z` : undefined)
                  }
                  className="w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm dark:border-gray-600 dark:bg-gray-800 dark:text-white"
                />
              </div>

              {/* Clear Filters */}
              {hasFilters && (
                <div className="flex items-end md:col-span-2 lg:col-span-4">
                  <button
                    type="button"
                    onClick={() => {
                      setFilters({ limit: 50, offset: 0 });
                      setSearchInput('');
                    }}
                    className="inline-flex items-center gap-2 text-sm font-medium text-indigo-600 hover:text-indigo-500 dark:text-indigo-400"
                  >
                    <X className="h-4 w-4" />
                    {t('audit.clear_filters')}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Results */}
        {!data?.logs || data.logs.length === 0 ? (
          <div className="rounded-md border border-dashed border-gray-300 px-4 py-12 text-center text-sm text-gray-500 dark:border-gray-700 dark:text-gray-400">
            {hasFilters || filters.search
              ? t('audit.no_results')
              : t('audit.empty')}
          </div>
        ) : (
          <>
            {/* Results Count */}
            <div className="mb-4 flex items-center justify-between text-sm text-gray-600 dark:text-gray-400">
              <span>
                {t('audit.showing_results', {
                  from: (filters.offset ?? 0) + 1,
                  to: Math.min((filters.offset ?? 0) + data.logs.length, data.total_count),
                  total: data.total_count,
                })}
              </span>
            </div>

            {/* Audit Log List */}
            <div className="space-y-2">
              {data.logs.map((log) => (
                <AuditLogItem key={log.id} log={log} />
              ))}
            </div>

            {/* Pagination */}
            {(data.has_more || (filters.offset ?? 0) > 0) && (
              <div className="mt-6 flex items-center justify-between border-t border-gray-200 pt-4 dark:border-gray-700">
                <button
                  type="button"
                  onClick={handlePrevPage}
                  disabled={(filters.offset ?? 0) === 0}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('audit.previous')}
                </button>
                <button
                  type="button"
                  onClick={handleNextPage}
                  disabled={!data.has_more}
                  className="rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  {t('audit.next')}
                </button>
              </div>
            )}
          </>
        )}
      </SectionCard>
    </div>
  );
}

function AuditLogItem({ log }: { log: AuditLogRow }) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);

  const categoryColor = {
    authentication: 'bg-blue-50 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    data_access: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
    data_modification: 'bg-yellow-50 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-300',
    cpr_access: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    export: 'bg-purple-50 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
    configuration: 'bg-indigo-50 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
    security: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  }[log.category];

  const date = new Date(log.created_at);
  const dateStr = date.toLocaleDateString();
  const timeStr = date.toLocaleTimeString();

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-4 dark:border-gray-700 dark:bg-gray-800">
      <div className="flex items-start justify-between">
        <div className="flex-1 space-y-2">
          {/* Header Row */}
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${categoryColor}`}
            >
              {t(`audit.category_${log.category}`)}
            </span>
            <span className="text-sm font-medium text-gray-900 dark:text-white">
              {t(`audit.action_${log.action}`, log.action)}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              <Calendar className="mr-1 inline h-3 w-3" />
              {dateStr} {timeStr}
            </span>
          </div>

          {/* Details Row */}
          <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600 dark:text-gray-400">
            {log.actor_email && (
              <span className="inline-flex items-center gap-1">
                <User className="h-3.5 w-3.5" />
                {log.actor_email}
              </span>
            )}
            {log.target_identifier && (
              <span className="inline-flex items-center gap-1">
                <FileText className="h-3.5 w-3.5" />
                {log.target_type}: {log.target_identifier}
              </span>
            )}
            {log.purpose && (
              <span className="italic text-gray-500 dark:text-gray-500">
                "{log.purpose}"
              </span>
            )}
          </div>
        </div>

        {/* Expand Button */}
        <button
          type="button"
          onClick={() => setExpanded(!expanded)}
          className="ml-4 rounded-md p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-700 dark:hover:text-gray-300"
        >
          {expanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
        </button>
      </div>

      {/* Expanded Details */}
      {expanded && (
        <div className="mt-4 space-y-2 border-t border-gray-200 pt-4 dark:border-gray-700">
          <DetailRow label={t('audit.detail_id')} value={log.id} />
          <DetailRow label={t('audit.detail_source')} value={log.source} />
          {log.source_function && (
            <DetailRow label={t('audit.detail_function')} value={log.source_function} />
          )}
          {log.actor_role && (
            <DetailRow label={t('audit.detail_role')} value={log.actor_role} />
          )}
          {log.actor_ip_address && (
            <DetailRow label={t('audit.detail_ip')} value={log.actor_ip_address} />
          )}
          {log.session_id && (
            <DetailRow label={t('audit.detail_session')} value={log.session_id} />
          )}
          {log.request_id && (
            <DetailRow label={t('audit.detail_request')} value={log.request_id} />
          )}
          {log.old_values && Object.keys(log.old_values).length > 0 && (
            <DetailRow
              label={t('audit.detail_old_values')}
              value={<pre className="text-xs">{JSON.stringify(log.old_values, null, 2)}</pre>}
            />
          )}
          {log.new_values && Object.keys(log.new_values).length > 0 && (
            <DetailRow
              label={t('audit.detail_new_values')}
              value={<pre className="text-xs">{JSON.stringify(log.new_values, null, 2)}</pre>}
            />
          )}
          {log.metadata && Object.keys(log.metadata).length > 0 && (
            <DetailRow
              label={t('audit.detail_metadata')}
              value={<pre className="text-xs">{JSON.stringify(log.metadata, null, 2)}</pre>}
            />
          )}
        </div>
      )}
    </div>
  );
}

function DetailRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="text-sm">
      <span className="font-medium text-gray-700 dark:text-gray-300">{label}:</span>{' '}
      <span className="text-gray-600 dark:text-gray-400">{value}</span>
    </div>
  );
}
